import { Button, Input, Popover, PopoverTrigger, PopoverContent, Slider } from "@heroui/react";
import { ProductObject } from "app/api/product/product.type";
import React, { useCallback, useLayoutEffect, useMemo, useEffect } from "react";
import { MdQuestionMark } from "react-icons/md";

interface SliderWithDynamicMarkersProps {
  productQty: number;
  data: {
    product: ProductObject["product"];
    slug: string;
    quantity: {
      max: number;
      min: number;
    };
  };
  // selectedColor: {
  //   qtyOnHand: number;
  // };
  handleQtyChange: (value: number) => void;
}

const SliderWithDynamicMarkers: React.FC<SliderWithDynamicMarkersProps> = ({
  // selectedColor,
  productQty,
  data,
  handleQtyChange,
}) => {
  const minQuantity = useMemo(() => data.quantity.min || 1, [data.quantity.min]);
  const maxQuantity = useMemo(
    () => minQuantity + (data.quantity.max || 10),
    [minQuantity, data.quantity.max],
  );

  const priceBreakMarkers = useMemo(() => {
    return data.product?.prices?.priceGroups?.[0]?.basePrice?.priceBreaks?.map((breakpoint) => ({
      value: breakpoint.qty,
      label: `${breakpoint.qty}`,
    }));
  }, [data.product]);

  const generateMarkers = useCallback(() => {
    if (priceBreakMarkers && priceBreakMarkers.length > 0) {
      const lastPriceBreak = priceBreakMarkers[priceBreakMarkers.length - 1];

      // If the last price break marker is less than 25, generate markers till 1000
      if (lastPriceBreak.value < 25) {
        const additionalMarkers = [];
        const startValue = lastPriceBreak.value;
        const range = 1000 - startValue;

        // Generate markers from last price break to 1000
        const stepBase = Math.pow(10, Math.floor(Math.log10(range)) - 1);
        const stepSize = Math.max(100, Math.ceil(range / 5 / stepBase) * stepBase);

        for (let value = startValue + stepSize; value <= 1000; value += stepSize) {
          additionalMarkers.push({ value, label: value.toString() });
        }

        // Always include 1000 as the last marker if it's not already there
        if (
          additionalMarkers.length === 0 ||
          additionalMarkers[additionalMarkers.length - 1]?.value !== 1000
        ) {
          additionalMarkers.push({ value: 1000, label: "1000" });
        }

        return [...priceBreakMarkers, ...additionalMarkers];
      }

      return priceBreakMarkers;
    }

    // Existing logic for when there are no price break markers
    const range = maxQuantity - minQuantity;
    if (range <= 0) return [{ value: minQuantity, label: `${minQuantity}` }];

    // Use fixed step size of 250 when there are no price break markers
    const stepSize = 250;

    const markers = [];
    for (let value = minQuantity; value <= maxQuantity; value += stepSize) {
      markers.push({ value, label: value.toString() });
    }

    if (markers[markers.length - 1]?.value !== maxQuantity) {
      markers.push({ value: maxQuantity, label: maxQuantity.toString() });
    }

    // If maxQuantity is less than 25, extend markers to 1000
    if (maxQuantity < 25) {
      const additionalMarkers = [];
      const startValue = maxQuantity;
      const extendedStepSize = 250; // Use the same step size for consistency

      for (let value = startValue + extendedStepSize; value <= 1000; value += extendedStepSize) {
        additionalMarkers.push({ value, label: value.toString() });
      }

      // Always include 1000 as the last marker if it's not already there
      if (
        additionalMarkers.length === 0 ||
        additionalMarkers[additionalMarkers.length - 1]?.value !== 1000
      ) {
        additionalMarkers.push({ value: 1000, label: "1000" });
      }

      return [...markers, ...additionalMarkers];
    }

    return markers;
  }, [minQuantity, maxQuantity, priceBreakMarkers]);

  const markers = useMemo(() => generateMarkers(), [generateMarkers]);

  // Set the initial quantity when the slug changes.
  useEffect(() => {
    handleQtyChange(minQuantity);
  }, [data.slug]);

  // Using useLayoutEffect to measure and adjust marker positions right after layout changes.
  useLayoutEffect(() => {
    if (!markers) return;

    // Marker elements are created by the slider library; ensure classes apply after they're present.
    const applyMarkerClasses = () => {
      const markerElements = document.querySelectorAll<HTMLElement>(".slider_maker");
      if (!markerElements.length) return false;

      markerElements.forEach((el, index) => {
        // Odd markers need a different vertical offset; no measurement needed.
        if (index % 2 === 1) el.classList.add("!-top-[2.6rem]");
      });

      return true;
    };

    // Try immediately, then retry on the next frame if the library hasn't rendered markers yet.
    if (!applyMarkerClasses()) {
      const rafId = window.requestAnimationFrame(() => applyMarkerClasses());
      return () => window.cancelAnimationFrame(rafId);
    }
  }, [markers, data.quantity.min]);

  return (
    <Slider
      label={
        <div className="mb-4">
          <span className="font-semibold text-gray-700 dark:text-gray-200">
            2. Select a Quantity
          </span>{" "}
          <Popover placement="top">
            <PopoverTrigger>
              <Button
                isIconOnly
                className="bg-transparent !p-0.5"
                aria-label="Quantity pricing info"
              >
                <MdQuestionMark size={18} className="text-primary dark:text-yellow text-xs" />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="px-1 py-2 max-w-60">
                <div className="text-small font-bold">Quantity Pricing</div>
                <div className="text-tiny">Discount applied with increase in quantity</div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      }
      startContent={
        <Input
          type="number"
          variant="bordered"
          className="w-24"
          classNames={{ inputWrapper: "dark:border-gray-400", label: "text-xs" }}
          label={`(Min. Qty ${minQuantity})`}
          labelPlacement="outside"
          value={productQty.toString()}
          min={minQuantity ? minQuantity : 1}
          // max={maxQuantity ? maxQuantity : selectedColor.qtyOnHand}
          disabled={maxQuantity === 0 || maxQuantity === minQuantity}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (!Number.isNaN(value)) handleQtyChange(value);
          }}
        />
      }
      hideValue
      value={productQty}
      onChange={(v) => handleQtyChange(v as number)}
      maxValue={(maxQuantity < 25 ? 1000 : maxQuantity) || 1000}
      minValue={minQuantity ? minQuantity : 1}
      marks={markers}
      className="w-full"
      classNames={{
        trackWrapper: " gap-y-8 flex-wrap md:flex-nowrap",
        mark: ["slider_maker"],
      }}
      defaultValue={[minQuantity, 1000]}
      showTooltip
      tooltipProps={{
        offset: 4,
        placement: "right",
      }}
      tooltipValueFormatOptions={{ style: "decimal", maximumFractionDigits: 0 }}
    />
  );
};

export default SliderWithDynamicMarkers;
